/**
 * User Store Implementation
 *
 * 사용자 인증 및 프로필 정보 관리를 위한 Zustand store 구현
 * localStorage를 통한 영속성 지원으로 브라우저 새로고침 시에도 로그인 상태 유지
 *
 * 주요 기능:
 * - 사용자 로그인/로그아웃 상태 관리
 * - 사용자 프로필 정보 저장 (ID, 이름, 이메일)
 * - localStorage를 통한 사용자 정보 영속화
 * - 로그아웃 시 모든 사용자 정보 초기화
 */

import { createStore } from 'zustand';
import { persist } from 'zustand/middleware';

/** 사용자 정보 인터페이스
 *
 * @interface UserInfo
 * @property {number} id - 사용자의 고유 식별자
 * @property {string} name - 사용자의 이름
 * @property {string} email - 사용자의 이메일 주소
 * @property {boolean} [isSystemAdmin] - 시스템 관리자 여부. 이 필드 추가 전에 저장된 세션에는 없으므로
 *   읽을 때 `?? false`로 일반 관리자로 취급한다 (판매 지점 관리의 URL 영향 액션 권한 분기).
 */
interface UserInfo {
  id: number;
  name: string;
  email: string;
  isSystemAdmin?: boolean;
}

/**
 * 사용자 상태를 나타내는 인터페이스
 *
 * @interface UserState
 * @property {UserInfo | undefined} info - 현재 로그인한 사용자의 정보, 로그아웃 상태일 경우 undefined
 */
interface UserState {
  info: UserInfo | undefined;
}

/**
 * 사용자 상태를 조작하는 액션들을 정의하는 인터페이스
 *
 * @interface UserActions
 */
interface UserActions {
  /** 현재 사용자 store 상태를 반환하는 getter 함수 */
  getUserStore: () => UserState;

  /** 사용자 로그인 시 사용자 정보를 설정하는 함수 */
  setUserInfo: (info: UserInfo) => void;

  /** 사용자 로그아웃 시 모든 사용자 정보를 초기화하는 함수 */
  clearUserStore: () => void;
}

/** UserState와 UserActions를 결합한 완전한 사용자 store 타입 */
export type UserStore = UserState & UserActions;

/** 사용자 store의 초기 상태 (로그아웃 상태) */
const defaultUserState: UserState = {
  info: undefined, // 초기에는 로그인하지 않은 상태
};

/**
 * 사용자 store 인스턴스를 생성하는 팩토리 함수
 *
 * Zustand의 persist 미들웨어를 사용하여 localStorage에 사용자 정보를 저장
 * 브라우저 새로고침 시에도 로그인 상태가 유지됨
 *
 * @param {UserState} [initState=defaultUserState] - 초기 상태 (선택적)
 * @returns {StoreApi<UserStore>} Zustand store 인스턴스
 */
export const createUserStore = (initState: UserState = defaultUserState) => {
  return createStore<UserStore>()(
    persist(
      (set, get) => ({
        ...initState,

        // Store 상태 전체를 반환하는 getter
        getUserStore: get,

        // 사용자 로그인 시 사용자 정보 설정
        setUserInfo: (info) => {
          set({ info });
        },

        // 사용자 로그아웃 시 초기 상태로 복원
        clearUserStore: () => {
          set(initState);
        },
      }),
      {
        name: USER_STORE_LOCALSTORAGE_KEY,
        // 사용자 정보만 localStorage에 저장
        partialize: ({ info }) => ({ info }),
      },
    ),
  );
};

/** localStorage에 사용자 정보를 저장할 때 사용하는 키 */
const USER_STORE_LOCALSTORAGE_KEY = 'user';
